import React, { useMemo, useState } from 'react';
import { Badge, Form } from 'react-bootstrap';

/**
 * #233007 — Ô CHỌN NCC của màn "Gửi đơn đặt hàng cho NCC qua Zalo".
 *
 * Vấn đề thật (lệnh sếp update_id=731599110, 23:28 27/08/2026): production chỉ có RẤT ÍT NCC được
 * điền `Supplier.zalo_phone`, nên trong ô chọn cũ (một `<Form.Select>` chỉ hiện mỗi tên) người dùng
 * rất dễ chọn nhầm NCC chưa có liên hệ, bấm Gửi mới biết là không gửi được.
 *
 * Bốn điều ô chọn này phải làm:
 * - Nhìn là biết ngay: mỗi dòng hiện luôn SĐT bên cạnh tên; NCC chưa có số thì ghi thẳng chữ
 *   "⚠ chưa có SĐT Zalo". CHỮ/BIỂU TƯỢNG chứ KHÔNG dựa vào màu — sếp dùng điện thoại 390×844,
 *   màu không đọc được khi liếc nhanh.
 * - NCC đã có liên hệ đứng TRƯỚC, tách thành 2 nhóm `<optgroup>` có tiêu đề + số lượng.
 * - Ô tìm theo tên (và theo cả SĐT) để khỏi cuộn tay khi danh sách dài.
 * - Ô tick "Chỉ hiện NCC đã có SĐT Zalo" — MẶC ĐỊNH TẮT, xem giải trình dưới.
 *
 * GIẢI TRÌNH mặc định TẮT bộ lọc: NCC *chưa* có số vẫn phải chọn được, vì luồng "chọn NCC → thấy
 * cảnh báo đỏ → bấm Thêm số Zalo ngay tại chỗ" (mục E của US-115332, `SupplierZaloPhoneEditor`) là
 * cách DUY NHẤT sếp bổ sung số cho NCC mới ngay trên màn này. Bật lọc sẵn sẽ giấu mất đúng nhóm NCC
 * đang cần bổ sung số (27/29 NCC ở dữ liệu thật), người dùng tưởng NCC "biến mất" khỏi hệ thống.
 * Việc phân biệt nhanh đã được nhóm + nhãn chữ lo rồi, nên không cần giấu dữ liệu theo mặc định.
 *
 * Nguồn dữ liệu: đúng trường `Supplier.zalo_phone` đang dùng — KHÔNG đẻ trường thứ hai.
 */

const hasZalo = (s) => !!String(s?.zalo_phone ?? '').trim();

/** Bỏ dấu tiếng Việt để gõ "quang nhung" cũng tìm ra "Quang Nhung". */
const norm = (v) =>
  String(v ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D')
    .toLowerCase()
    .trim();

const byName = (a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'vi');

export default function SupplierZaloPicker({ suppliers = [], value = '', onChange }) {
  const [search, setSearch] = useState('');
  const [onlyWithPhone, setOnlyWithPhone] = useState(false);

  const active = useMemo(
    () => (suppliers || []).filter((s) => s.is_active !== false),
    [suppliers],
  );

  const withPhone = useMemo(() => active.filter(hasZalo).sort(byName), [active]);
  const withoutPhone = useMemo(() => active.filter((s) => !hasZalo(s)).sort(byName), [active]);

  const selected = useMemo(() => active.find((s) => s.id === value) || null, [active, value]);

  const matches = (s) => {
    const q = norm(search);
    if (!q) return true;
    return norm(s.name).includes(q) || norm(s.zalo_phone).includes(q);
  };

  // NCC đang được chọn LUÔN nằm trong danh sách, kể cả khi bộ lọc/ô tìm loại nó ra — nếu không,
  // `<select>` sẽ mất option của giá trị hiện tại và tự nhảy về dòng đầu, đổi NCC ngoài ý muốn.
  const keep = (s) => s.id === value;

  const shownWith = withPhone.filter((s) => keep(s) || matches(s));
  const shownWithout = onlyWithPhone
    ? withoutPhone.filter(keep)
    : withoutPhone.filter((s) => keep(s) || matches(s));

  const totalShown = shownWith.length + shownWithout.length;

  const optionLabel = (s) =>
    hasZalo(s) ? `${s.name} · ${s.zalo_phone}` : `${s.name} · ⚠ chưa có SĐT Zalo`;

  return (
    <div data-testid="supplier-picker">
      <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
        <Form.Control
          type="search"
          size="sm"
          style={{ maxWidth: 260 }}
          placeholder="Tìm NCC theo tên hoặc SĐT…"
          data-testid="supplier-search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Form.Check
          type="checkbox"
          id="only-with-zalo-phone"
          className="small"
          data-testid="only-with-phone"
          label="Chỉ hiện NCC đã có SĐT Zalo"
          checked={onlyWithPhone}
          onChange={(e) => setOnlyWithPhone(e.target.checked)}
        />
      </div>

      <div className="small text-muted mb-2" data-testid="supplier-counts">
        <Badge bg="success" className="me-1">{withPhone.length}</Badge> NCC đã có SĐT Zalo ·{' '}
        <Badge bg="secondary" className="me-1">{withoutPhone.length}</Badge> chưa có
        {search.trim() && <> · đang lọc theo “{search.trim()}”</>}
      </div>

      <Form.Select
        data-testid="supplier-select"
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
      >
        <option value="">-- Chọn nhà cung cấp --</option>
        {shownWith.length > 0 && (
          <optgroup label={`✅ Đã có SĐT Zalo (${shownWith.length}) — gửi được ngay`}>
            {shownWith.map((s) => (
              <option key={s.id} value={s.id}>{optionLabel(s)}</option>
            ))}
          </optgroup>
        )}
        {shownWithout.length > 0 && (
          <optgroup label={`⚠ Chưa có SĐT Zalo (${shownWithout.length}) — phải thêm số mới gửi được`}>
            {shownWithout.map((s) => (
              <option key={s.id} value={s.id}>{optionLabel(s)}</option>
            ))}
          </optgroup>
        )}
      </Form.Select>

      {totalShown === 0 && (
        <div className="text-muted small mt-2" data-testid="supplier-empty">
          {onlyWithPhone && withPhone.length === 0
            ? 'Chưa có NCC nào được điền SĐT Zalo. Bỏ tick bộ lọc để chọn NCC và thêm số ngay tại đây.'
            : 'Không có NCC nào khớp từ khoá đang tìm.'}
        </div>
      )}

      {/* Trạng thái của NCC ĐANG CHỌN lặp lại bằng chữ ngay dưới ô select: trên iOS/Android phần
          `<optgroup>` không hiện lại sau khi đóng picker, nên đây là chỗ duy nhất người dùng nhìn
          thấy mình vừa chọn nhóm nào. Cảnh báo đỏ + nút "Thêm số Zalo" do
          `SupplierZaloPhoneEditor` (US-115332 mục E) render ngay bên dưới, không viết lại ở đây. */}
      {selected && (
        <div className="small mt-2" data-testid="supplier-selected-status">
          Đang chọn: <strong>{selected.name}</strong> —{' '}
          {hasZalo(selected) ? (
            <span className="text-success fw-semibold">✅ đã có SĐT Zalo {selected.zalo_phone}</span>
          ) : (
            <span className="text-danger fw-semibold">⚠ CHƯA có SĐT Zalo, chưa gửi được</span>
          )}
        </div>
      )}
    </div>
  );
}
